import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import type { Region, HistoricalEvent } from '../types'
import countriesGeo from '../data/geo/countries.json'
import indiaOfficialGeo from '../data/geo/india_official.json'
import { useTheme } from '../hooks/useTheme'

interface Props {
  regions: Region[]
  events: HistoricalEvent[]                  // events that match the current time selection
  hasTimeFilter: boolean                     // true when an era or year is selected; controls whether we hide inactive markers
  selectedRegionId: string | null
  hoverRegionId?: string | null
  onSelectRegion: (regionId: string | null) => void
  onHoverRegion?: (regionId: string | null) => void
}

// =============================================================================
// World coordinate frame.
// All geometry (country polygons, region markers) is projected once into a
// fixed equirectangular world coordinate system that is large enough to hold
// any plausible Greater India scope — west to the Iranian plateau (LON 30) and
// east to Java (LON 130), south to below the equator (LAT -15) and north to
// Central Asia (LAT 55). The visible SVG viewBox then slides over this world
// frame and is sized dynamically to the regions currently in scope.
//
//   project(lon, lat) = [(lon) * SCALE, (LAT_TOP - lat) * SCALE]
//
// With LAT_TOP = 55 and SCALE = 20 px/°, the world coords are positive and
// north-is-up. The viewBox is expressed in the same units.
// =============================================================================
const WORLD_SCALE = 20
const WORLD_LAT_TOP = 55

// Ring-culling envelope for country geometry — features whose bbox sits
// entirely outside this window are dropped once at load time, so we never
// render Russia / sub-Saharan Africa / mid-Pacific islands.
const RING_LON_MIN = 30,  RING_LON_MAX = 130
const RING_LAT_MIN = -15, RING_LAT_MAX = 55

// Margin around the data bbox when computing the visible viewBox, in degrees.
// 4° ≈ 440 km — enough to keep ocean and neighbouring polygons visible around
// the dataset without making single-region selections feel teleported.
const VIEWBOX_PAD_DEG = 4

// Default container size used for the first render, before ResizeObserver
// reports the real dimensions. Picked to roughly match a lg viewport pane.
const DEFAULT_CONTAINER = { w: 800, h: 600 }

// "Heart of Bhāratavarṣa" — the bbox used when no time/figure filter is active.
// Deliberately tighter than the full union of `regions` so the homepage view
// keeps India legible; far-flung points like west_asia (lon 44) and yavadvipa
// (lat -7) are scope-relevant but not appropriate as default-view anchors.
// Already includes the immediate limbs (Bactria, Gandhāra, Sri Lanka, Burma).
const DEFAULT_BOUNDS = { lonMin: 60, lonMax: 110, latMin: 5, latMax: 38 }

// User-zoom bounds: 0.5× lets you zoom OUT past auto-fit (useful to glance at
// neighbouring context); 20× lets you zoom WAY into a single district.
const MIN_ZOOM = 0.5
const MAX_ZOOM = 20
// Wheel sensitivity. deltaY positive = scroll down = zoom out (matches map
// conventions). exp(-deltaY * WHEEL_SENS) gives smooth, multiplicative zoom.
const WHEEL_SENS = 0.0015
// Button click step (zoom in/out multiplier).
const BUTTON_ZOOM_STEP = 1.5

export function project(lon: number, lat: number): [number, number] {
  return [lon * WORLD_SCALE, (WORLD_LAT_TOP - lat) * WORLD_SCALE]
}

// -----------------------------------------------------------------------------
// GeoJSON → SVG path conversion. Rings outside the broad world envelope are
// dropped here so we never even consider them during render. Inside the
// envelope, every coordinate is projected through project() into world units.
// -----------------------------------------------------------------------------
type GeoFeature = {
  type: 'Feature'
  properties: { name: string; iso: string; tag: 'india' | 'subcontinent' | 'southeastAsia' | 'westAsia' | 'china' }
  geometry:
    | { type: 'Polygon';      coordinates: number[][][] }
    | { type: 'MultiPolygon'; coordinates: number[][][][] }
}
const COUNTRIES = countriesGeo as { type: 'FeatureCollection'; features: GeoFeature[] }
const INDIA_OFFICIAL = indiaOfficialGeo as { type: 'FeatureCollection'; features: GeoFeature[] }

function ringInsideEnvelope(ring: number[][]): boolean {
  let lonMin =  Infinity, lonMax = -Infinity
  let latMin =  Infinity, latMax = -Infinity
  for (const [lon, lat] of ring) {
    if (lon < lonMin) lonMin = lon
    if (lon > lonMax) lonMax = lon
    if (lat < latMin) latMin = lat
    if (lat > latMax) latMax = lat
  }
  return lonMax >= RING_LON_MIN &&
         lonMin <= RING_LON_MAX &&
         latMax >= RING_LAT_MIN &&
         latMin <= RING_LAT_MAX
}

function ringToPath(ring: number[][]): string {
  let s = ''
  for (let i = 0; i < ring.length; i++) {
    const [x, y] = project(ring[i][0], ring[i][1])
    s += (i === 0 ? 'M' : 'L') + x.toFixed(1) + ',' + y.toFixed(1) + ' '
  }
  return s + 'Z'
}

function featureToPath(f: GeoFeature): string {
  const polygons: number[][][][] =
    f.geometry.type === 'Polygon' ? [f.geometry.coordinates] : f.geometry.coordinates
  const out: string[] = []
  for (const poly of polygons) {
    for (const ring of poly) {
      if (ringInsideEnvelope(ring)) out.push(ringToPath(ring))
    }
  }
  return out.join(' ')
}

// -----------------------------------------------------------------------------
// Theme palettes. The map's SVG can't read CSS overrides cleanly because every
// fill / stroke is set as an inline attribute, so we drive colours from JS
// against the current theme.
// -----------------------------------------------------------------------------
type Tag = GeoFeature['properties']['tag']
type TagStyle = { fill: string; stroke: string; strokeOpacity: number; opacity: number }

interface MapPalette {
  sea: string
  seaOpacity: number
  tagStyle: Record<Tag, TagStyle>
  marker: {
    activeHalo: string;     activeHaloOpacity: number
    activeFill: string;     activeStroke: string
    inactiveFill: string;   inactiveStroke: string
    selectedFill: string;   selectedStroke: string
    labelFill: string;      labelSelectedFill: string
    labelStroke: string;    labelStrokeOpacity: number
  }
  creditFill: string
  labelFontFamily: string
}

const PARCHMENT_PALETTE: MapPalette = {
  sea: '#EDE6D4',
  seaOpacity: 0.35,
  tagStyle: {
    india:         { fill: 'url(#paper)',     stroke: '#26211B', strokeOpacity: 0.55, opacity: 1.00 },
    subcontinent:  { fill: 'url(#paperWarm)', stroke: '#26211B', strokeOpacity: 0.40, opacity: 0.95 },
    southeastAsia: { fill: 'url(#paperWarm)', stroke: '#26211B', strokeOpacity: 0.32, opacity: 0.90 },
    westAsia:      { fill: 'url(#paperCool)', stroke: '#26211B', strokeOpacity: 0.30, opacity: 0.85 },
    china:         { fill: 'url(#paperCool)', stroke: '#26211B', strokeOpacity: 0.22, opacity: 0.80 }
  },
  marker: {
    activeHalo:        '#BA7517', activeHaloOpacity: 0.12,
    activeFill:        '#EF9F27', activeStroke:     '#854F0B',
    inactiveFill:      '#F8F2E6', inactiveStroke:   '#26211B',
    selectedFill:      '#BA7517', selectedStroke:   '#412402',
    labelFill:         '#26211B', labelSelectedFill:'#412402',
    labelStroke:       '#FDFAF2', labelStrokeOpacity: 0.85
  },
  creditFill: '#544A3A',
  labelFontFamily: "'Cormorant Garamond', Georgia, serif"
}

const BROADSHEET_PALETTE: MapPalette = {
  sea: '#EFEFEC',
  seaOpacity: 1.0,
  tagStyle: {
    india:         { fill: '#FFFFFF', stroke: '#1A1A1A', strokeOpacity: 0.85, opacity: 1.00 },
    subcontinent:  { fill: '#FAFAF8', stroke: '#1A1A1A', strokeOpacity: 0.45, opacity: 1.00 },
    southeastAsia: { fill: '#F6F6F3', stroke: '#1A1A1A', strokeOpacity: 0.35, opacity: 1.00 },
    westAsia:      { fill: '#F4F4F2', stroke: '#1A1A1A', strokeOpacity: 0.30, opacity: 1.00 },
    china:         { fill: '#F2F2EF', stroke: '#1A1A1A', strokeOpacity: 0.20, opacity: 1.00 }
  },
  marker: {
    activeHalo:        '#326891', activeHaloOpacity: 0.18,
    activeFill:        '#326891', activeStroke:     '#1A4163',
    inactiveFill:      '#FFFFFF', inactiveStroke:   '#5A5A58',
    selectedFill:      '#0E3960', selectedStroke:   '#082541',
    labelFill:         '#1A1A1A', labelSelectedFill:'#0E3960',
    labelStroke:       '#FFFFFF', labelStrokeOpacity: 0.95
  },
  creditFill: '#7A7A78',
  labelFontFamily: "'Helvetica Neue', Helvetica, Arial, sans-serif"
}

const MAP_PALETTES: Record<string, MapPalette> = {
  parchment:  PARCHMENT_PALETTE,
  broadsheet: BROADSHEET_PALETTE
}

// -----------------------------------------------------------------------------
// Pick the regions whose bbox should drive the visible viewBox.
//
// Rules:
//   • If a time filter is active and there are active regions in that window,
//     fit to those (plus the selected region, even if it has no events there).
//     This is the magic: Vol 1 era → tight Vedic/Indus view; Vol 6 era →
//     expands to include Cōḻa maritime reach.
//   • Otherwise — homepage, figure-only filter, region-only selection — use
//     the curated DEFAULT_BOUNDS so India stays legible at default zoom.
//     Single-region selection does NOT zoom in; the user just gets the
//     marker highlighted within the wider context.
//
// Returns a bounding box in geographic (lon/lat) degrees, already padded.
// -----------------------------------------------------------------------------
function pickViewBoxBounds(
  regions: Region[],
  activeRegionIds: Set<string>,
  selectedRegionId: string | null,
  hasTimeFilter: boolean
): { lonMin: number; lonMax: number; latMin: number; latMax: number } {
  if (!hasTimeFilter || activeRegionIds.size === 0) {
    return DEFAULT_BOUNDS
  }

  const pool = regions.filter(r => activeRegionIds.has(r.id))
  if (selectedRegionId) {
    const sel = regions.find(r => r.id === selectedRegionId)
    if (sel && !pool.includes(sel)) pool.push(sel)
  }

  let lonMin =  Infinity, lonMax = -Infinity
  let latMin =  Infinity, latMax = -Infinity
  for (const r of pool) {
    const c = r.modernCenter
    if (!c || typeof c.lon !== 'number' || typeof c.lat !== 'number'
          || Number.isNaN(c.lon) || Number.isNaN(c.lat)) continue
    if (c.lon < lonMin) lonMin = c.lon
    if (c.lon > lonMax) lonMax = c.lon
    if (c.lat < latMin) latMin = c.lat
    if (c.lat > latMax) latMax = c.lat
  }

  // Fallback if pool was empty or all centers were malformed.
  if (!Number.isFinite(lonMin)) {
    return DEFAULT_BOUNDS
  }

  return {
    lonMin: lonMin - VIEWBOX_PAD_DEG,
    lonMax: lonMax + VIEWBOX_PAD_DEG,
    latMin: latMin - VIEWBOX_PAD_DEG,
    latMax: latMax + VIEWBOX_PAD_DEG
  }
}

// -----------------------------------------------------------------------------
// Given a data bbox (degrees) and a container size (px), return the SVG
// viewBox in WORLD COORDS that (a) contains the data bbox entirely and
// (b) matches the container aspect ratio. The data is centered inside the
// returned viewBox. With matched aspect, preserveAspectRatio reduces to a
// no-op — no letterbox, no crop.
// -----------------------------------------------------------------------------
function fitViewBox(
  bounds: { lonMin: number; lonMax: number; latMin: number; latMax: number },
  container: { w: number; h: number }
): { x: number; y: number; w: number; h: number } {
  const dataW = (bounds.lonMax - bounds.lonMin) * WORLD_SCALE
  const dataH = (bounds.latMax - bounds.latMin) * WORLD_SCALE
  const containerAspect = container.w / container.h
  const dataAspect = dataW / dataH

  let vbW = dataW, vbH = dataH
  if (containerAspect > dataAspect) {
    // Container is wider than data — extend longitude to fill horizontally.
    vbW = dataH * containerAspect
  } else {
    // Container is taller — extend latitude to fill vertically.
    vbH = dataW / containerAspect
  }

  const lonCenter = (bounds.lonMin + bounds.lonMax) / 2
  const latCenter = (bounds.latMin + bounds.latMax) / 2
  const vbLonMin = lonCenter - (vbW / WORLD_SCALE) / 2
  const vbLatMax = latCenter + (vbH / WORLD_SCALE) / 2

  return {
    x: vbLonMin * WORLD_SCALE,
    y: (WORLD_LAT_TOP - vbLatMax) * WORLD_SCALE,
    w: vbW,
    h: vbH
  }
}

export default function MapView({
  regions,
  events,
  hasTimeFilter,
  selectedRegionId,
  hoverRegionId,
  onSelectRegion,
  onHoverRegion
}: Props) {
  const { theme } = useTheme()
  const palette = MAP_PALETTES[theme] ?? PARCHMENT_PALETTE

  // -- Container size tracking ------------------------------------------------
  const wrapperRef = useRef<HTMLDivElement | null>(null)
  const [containerSize, setContainerSize] = useState(DEFAULT_CONTAINER)

  useEffect(() => {
    const el = wrapperRef.current
    if (!el) return
    const ro = new ResizeObserver(entries => {
      for (const entry of entries) {
        const cr = entry.contentRect
        // Guard against zero-sized layout transients (e.g., during hot reload).
        if (cr.width > 0 && cr.height > 0) {
          setContainerSize({ w: cr.width, h: cr.height })
        }
      }
    })
    ro.observe(el)
    return () => ro.disconnect()
  }, [])

  // -- Country paths (one-time, in world coords) -----------------------------
  const countryPaths = useMemo(() => {
    return COUNTRIES.features.map(f => ({
      iso:  f.properties.iso,
      name: f.properties.name,
      tag:  f.properties.tag,
      d:    featureToPath(f)
    })).filter(p => p.d.length > 0)
  }, [])

  const indiaPaths = useMemo(() => {
    return INDIA_OFFICIAL.features.map(f => ({
      iso:  f.properties.iso,
      name: f.properties.name,
      tag:  f.properties.tag,
      d:    featureToPath(f)
    })).filter(p => p.d.length > 0)
  }, [])

  // -- Active regions for current filter -------------------------------------
  const activeRegionIds = useMemo(() => {
    const s = new Set<string>()
    for (const e of events) for (const r of e.regionIds) s.add(r)
    return s
  }, [events])

  // -- Dynamic viewBox: bounds → aspect-fit → world coords -------------------
  // We compute it in two layers:
  //   1. `autoFit` — the "ideal" viewBox derived from current filter state and
  //      container size. This is what the map shows when there's no manual
  //      zoom override.
  //   2. `viewBox` — the actual rendered viewBox, which is `autoFit` shrunk
  //      and recentered by `manualZoom` when the user has scrolled or panned.
  const autoFit = useMemo(() => {
    const bounds = pickViewBoxBounds(regions, activeRegionIds, selectedRegionId, hasTimeFilter)
    return fitViewBox(bounds, containerSize)
  }, [regions, activeRegionIds, selectedRegionId, hasTimeFilter, containerSize])

  // Manual zoom + pan state. null = "auto-fit is canonical, no user override".
  // When set, the rendered viewBox = autoFit shrunk by `scale` and centered on
  // (centerX, centerY) in world coords.
  const [manualZoom, setManualZoom] = useState<{ scale: number; centerX: number; centerY: number } | null>(null)

  const viewBox = useMemo(() => {
    if (!manualZoom) return autoFit
    const w = autoFit.w / manualZoom.scale
    const h = autoFit.h / manualZoom.scale
    return {
      x: manualZoom.centerX - w / 2,
      y: manualZoom.centerY - h / 2,
      w,
      h
    }
  }, [autoFit, manualZoom])

  // -- Filter-change reset: when the auto-fit viewBox changes due to a new
  // era/figure/year selection, drop the manual override so the new auto-fit
  // takes effect. Selecting a region alone doesn't change the auto-fit bounds
  // (unless a time filter is also active), so manual zoom survives region
  // clicks. Container-size changes don't reset either — resizing the window
  // shouldn't yank you out of your zoomed view.
  useEffect(() => {
    setManualZoom(null)
    // Reset key intentionally excludes containerSize and selectedRegionId.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [events, hasTimeFilter])

  const vbStr = `${viewBox.x.toFixed(1)} ${viewBox.y.toFixed(1)} ${viewBox.w.toFixed(1)} ${viewBox.h.toFixed(1)}`

  // -- Manual zoom / pan handlers --------------------------------------------
  // We need the current viewBox + container size inside event handlers without
  // re-binding listeners on every render, so stash both in refs that always
  // reflect the latest render.
  const viewBoxRef = useRef(viewBox)
  const autoFitRef = useRef(autoFit)
  const containerSizeRef = useRef(containerSize)
  viewBoxRef.current = viewBox
  autoFitRef.current = autoFit
  containerSizeRef.current = containerSize

  // Convert a pointer's clientX/Y → world coords using the wrapping div's
  // bounding rect. Because viewBox aspect matches container aspect (the
  // `fitViewBox` invariant), this is a direct proportional mapping; no need
  // to compensate for `slice` crop.
  const clientToWorld = useCallback((clientX: number, clientY: number): [number, number] | null => {
    const el = wrapperRef.current
    if (!el) return null
    const rect = el.getBoundingClientRect()
    if (rect.width === 0 || rect.height === 0) return null
    const vb = viewBoxRef.current
    const relX = (clientX - rect.left) / rect.width
    const relY = (clientY - rect.top) / rect.height
    return [vb.x + relX * vb.w, vb.y + relY * vb.h]
  }, [])

  // Apply a scale change anchored at a given world point — the world point
  // stays under the same cursor pixel before and after the zoom.
  const zoomAtWorld = useCallback((worldX: number, worldY: number, scaleMultiplier: number) => {
    setManualZoom(prev => {
      const af = autoFitRef.current
      const currentScale = prev?.scale ?? 1
      const newScale = Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, currentScale * scaleMultiplier))
      if (newScale === currentScale) return prev
      // Current viewBox derived from prev. Compute what fraction the world
      // anchor sits at inside that viewBox, then keep it at the same fraction
      // in the post-zoom viewBox.
      const currentW = af.w / currentScale
      const currentH = af.h / currentScale
      const currentX = (prev?.centerX ?? (af.x + af.w / 2)) - currentW / 2
      const currentY = (prev?.centerY ?? (af.y + af.h / 2)) - currentH / 2
      const relX = (worldX - currentX) / currentW
      const relY = (worldY - currentY) / currentH
      const newW = af.w / newScale
      const newH = af.h / newScale
      const newX = worldX - relX * newW
      const newY = worldY - relY * newH
      return {
        scale: newScale,
        centerX: newX + newW / 2,
        centerY: newY + newH / 2
      }
    })
  }, [])

  // Wheel listener. React's onWheel is passive, which prevents preventDefault;
  // we attach a non-passive listener directly so the page doesn't scroll while
  // the user is zooming the map.
  useEffect(() => {
    const el = wrapperRef.current
    if (!el) return
    const onWheel = (e: WheelEvent) => {
      // Trackpad pinch on macOS fires wheel events with ctrlKey set. Treat
      // those identically to wheel zoom. Non-pinch trackpad scrolls also fall
      // through here; we treat any wheel-over-map as a zoom intent.
      e.preventDefault()
      const world = clientToWorld(e.clientX, e.clientY)
      if (!world) return
      const scaleMultiplier = Math.exp(-e.deltaY * WHEEL_SENS)
      zoomAtWorld(world[0], world[1], scaleMultiplier)
    }
    el.addEventListener('wheel', onWheel, { passive: false })
    return () => el.removeEventListener('wheel', onWheel)
  }, [clientToWorld, zoomAtWorld])

  // -- Pan via mouse drag ----------------------------------------------------
  // dragStartRef holds enough state to compute the new center from any
  // mousemove without re-deriving things. Pixel deltas convert to world
  // deltas via the current viewBox-to-container ratio (which equals the
  // ratio at drag-start because we don't allow scaling mid-drag).
  const dragStartRef = useRef<{
    startClientX: number
    startClientY: number
    startCenterX: number
    startCenterY: number
    pxPerWorldX: number
    pxPerWorldY: number
    moved: boolean
  } | null>(null)

  const [isDragging, setIsDragging] = useState(false)

  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    if (e.button !== 0) return // left-click only
    const el = wrapperRef.current
    if (!el) return
    const rect = el.getBoundingClientRect()
    if (rect.width === 0 || rect.height === 0) return
    const vb = viewBoxRef.current
    // Capture drag state but do NOT setManualZoom yet — that happens only
    // after movement exceeds the click/drag threshold. Otherwise every
    // single-click on a region marker would flip us into the "manually
    // zoomed" state and light up the reset button for no visible reason.
    dragStartRef.current = {
      startClientX: e.clientX,
      startClientY: e.clientY,
      startCenterX: vb.x + vb.w / 2,
      startCenterY: vb.y + vb.h / 2,
      pxPerWorldX: rect.width / vb.w,
      pxPerWorldY: rect.height / vb.h,
      moved: false
    }
    setIsDragging(true)
  }, [])

  useEffect(() => {
    if (!isDragging) return
    const onMove = (e: MouseEvent) => {
      const ds = dragStartRef.current
      if (!ds) return
      const dxPx = e.clientX - ds.startClientX
      const dyPx = e.clientY - ds.startClientY
      // Only count as a real drag after a 3px threshold — below that, treat
      // it as a click (so region selection still works through mousedown).
      if (!ds.moved && Math.abs(dxPx) + Math.abs(dyPx) < 3) return
      ds.moved = true
      const dxWorld = dxPx / ds.pxPerWorldX
      const dyWorld = dyPx / ds.pxPerWorldY
      // First real movement: initialize manualZoom (preserving any existing
      // scale, defaulting to 1) and shift from the drag-start center.
      setManualZoom(prev => ({
        scale: prev?.scale ?? 1,
        centerX: ds.startCenterX - dxWorld,
        centerY: ds.startCenterY - dyWorld
      }))
    }
    const onUp = () => {
      setIsDragging(false)
      // Keep dragStartRef.current.moved so click handlers can read it; we'll
      // null the ref on next mousedown.
    }
    document.addEventListener('mousemove', onMove)
    document.addEventListener('mouseup', onUp)
    return () => {
      document.removeEventListener('mousemove', onMove)
      document.removeEventListener('mouseup', onUp)
    }
  }, [isDragging])

  // Suppress region-marker clicks at the end of a pan-drag (otherwise letting
  // go over a marker would also select it). Wraps the existing handler.
  const handleRegionClick = useCallback((regionId: string) => {
    if (dragStartRef.current?.moved) {
      dragStartRef.current.moved = false
      return
    }
    onSelectRegion(regionId === selectedRegionId ? null : regionId)
  }, [onSelectRegion, selectedRegionId])

  // -- Zoom buttons -----------------------------------------------------------
  const zoomByButton = useCallback((multiplier: number) => {
    const vb = viewBoxRef.current
    // Zoom around the center of the current viewBox.
    zoomAtWorld(vb.x + vb.w / 2, vb.y + vb.h / 2, multiplier)
  }, [zoomAtWorld])

  const resetZoom = useCallback(() => setManualZoom(null), [])

  const isZoomed = manualZoom !== null

  // Marker and label sizing strategy: keep their RENDERED screen size roughly
  // constant across zoom levels. With `slice` the SVG scales viewBox to fit
  // container by max(container.w/vbW, container.h/vbH); we invert that scale
  // so a 4.5-screen-px marker at default zoom is still a 4.5-screen-px marker
  // when zoomed tight into a single region.
  const screenPxPerVbUnit = Math.max(
    containerSize.w / viewBox.w,
    containerSize.h / viewBox.h
  ) || 1
  const vbPx = (screenPx: number) => screenPx / screenPxPerVbUnit
  const baseR             = vbPx(4.5)
  const activeR           = vbPx(7.0)
  const hoverR            = vbPx(9.0)
  const selectR           = vbPx(11.0)
  const labelSizeBase     = vbPx(11)
  const labelSizeSelected = vbPx(14)
  const labelStrokeWidth  = vbPx(3)
  const labelOffsetGap    = vbPx(4)
  const creditFontSize    = vbPx(10)
  const haloPad           = vbPx(4)
  const haloRing          = vbPx(6)

  return (
    <div
      ref={wrapperRef}
      onMouseDown={handleMouseDown}
      className={
        "absolute inset-0 bg-parchment-50/40 rounded-md border border-ink-300/8 overflow-hidden select-none " +
        (isDragging ? "cursor-grabbing" : "cursor-grab")
      }
    >
      <svg
        viewBox={vbStr}
        preserveAspectRatio="xMidYMid slice"
        className="w-full h-full block"
        role="img"
        aria-label="Map of Greater India with historical regions"
      >
        <defs>
          {/* Parchment textures keyed to the band a country falls into. */}
          <pattern id="paper" patternUnits="userSpaceOnUse" width="6" height="6">
            <rect width="6" height="6" fill="#FDFAF2"/>
            <circle cx="3" cy="3" r="0.3" fill="#BA7517" opacity="0.05"/>
          </pattern>
          <pattern id="paperWarm" patternUnits="userSpaceOnUse" width="6" height="6">
            <rect width="6" height="6" fill="#FAF3E2"/>
            <circle cx="3" cy="3" r="0.3" fill="#BA7517" opacity="0.04"/>
          </pattern>
          <pattern id="paperCool" patternUnits="userSpaceOnUse" width="6" height="6">
            <rect width="6" height="6" fill="#F4F1E6"/>
            <circle cx="3" cy="3" r="0.3" fill="#26215C" opacity="0.03"/>
          </pattern>
        </defs>

        {/* Sea / negative space — sits underneath every country fill, sized to
            the current viewBox so it always covers regardless of zoom. */}
        <rect
          x={viewBox.x}
          y={viewBox.y}
          width={viewBox.w}
          height={viewBox.h}
          fill={palette.sea}
          opacity={palette.seaOpacity}
        />

        {/* Country polygons. India (Survey-of-India boundary) is rendered last
            so the official cartographic position — full pre-1947 J&K including
            PoK, Gilgit-Baltistan, Aksai Chin, and the Trans-Karakoram Tract —
            paints over the upstream Pakistan/China polygons. */}
        <g>
          {countryPaths.map(c => {
            const style = palette.tagStyle[c.tag] ?? palette.tagStyle.china
            return (
              <path
                key={c.iso}
                d={c.d}
                fill={style.fill}
                stroke={style.stroke}
                strokeOpacity={style.strokeOpacity}
                strokeWidth={0.6}
                opacity={style.opacity}
              >
                <title>{c.name}</title>
              </path>
            )
          })}
          {indiaPaths.map(c => {
            const style = palette.tagStyle.india
            return (
              <path
                key={c.iso}
                d={c.d}
                fill={style.fill}
                stroke={style.stroke}
                strokeOpacity={style.strokeOpacity}
                strokeWidth={0.6}
                opacity={style.opacity}
              >
                <title>{c.name}</title>
              </path>
            )
          })}
        </g>

        {/* Region markers — historical Janapadas / dynasties / cities. */}
        <g>
          {regions.map(r => {
            if (!r.modernCenter ||
                typeof r.modernCenter.lon !== 'number' ||
                typeof r.modernCenter.lat !== 'number' ||
                Number.isNaN(r.modernCenter.lon) ||
                Number.isNaN(r.modernCenter.lat)) {
              return null
            }
            const [x, y] = project(r.modernCenter.lon, r.modernCenter.lat)
            const isActive = activeRegionIds.has(r.id)
            const isSelected = r.id === selectedRegionId
            const isHovered = r.id === hoverRegionId

            // When a time filter is on, suppress markers that don't have an
            // event in this window. Selected and hovered remain visible.
            if (hasTimeFilter && !isActive && !isSelected && !isHovered) {
              return null
            }
            const baseRadius = isSelected ? selectR : isHovered ? hoverR : isActive ? activeR : baseR

            return (
              <g
                key={r.id}
                style={{ cursor: 'pointer' }}
                onClick={() => handleRegionClick(r.id)}
                onMouseEnter={() => onHoverRegion?.(r.id)}
                onMouseLeave={() => onHoverRegion?.(null)}
              >
                <circle
                  cx={x} cy={y} r={baseRadius + haloPad}
                  fill="transparent"
                />
                {isActive && (
                  <circle cx={x} cy={y} r={baseRadius + haloRing} fill={palette.marker.activeHalo} opacity={palette.marker.activeHaloOpacity} />
                )}
                <circle
                  cx={x} cy={y} r={baseRadius}
                  fill={isSelected ? palette.marker.selectedFill : isActive ? palette.marker.activeFill : palette.marker.inactiveFill}
                  stroke={isSelected ? palette.marker.selectedStroke : isActive ? palette.marker.activeStroke : palette.marker.inactiveStroke}
                  strokeOpacity={isSelected ? 1 : 0.4}
                  strokeWidth={isSelected ? 1.5 : 0.8}
                />
                {(isSelected || isHovered || isActive) && (
                  <text
                    x={x}
                    y={y - baseRadius - labelOffsetGap}
                    textAnchor="middle"
                    fontFamily={palette.labelFontFamily}
                    fontSize={isSelected ? labelSizeSelected : labelSizeBase}
                    fontWeight={isSelected ? 500 : 400}
                    fill={isSelected ? palette.marker.labelSelectedFill : palette.marker.labelFill}
                    className="iast pointer-events-none"
                    paintOrder="stroke"
                    stroke={palette.marker.labelStroke}
                    strokeWidth={labelStrokeWidth}
                    strokeOpacity={palette.marker.labelStrokeOpacity}
                  >
                    {r.primaryNameIAST ?? r.primaryName}
                  </text>
                )}
              </g>
            )
          })}
        </g>

        {/* Credit, pinned to the bottom-left of the current viewBox. */}
        <g transform={`translate(${viewBox.x + vbPx(12)}, ${viewBox.y + viewBox.h - vbPx(8)})`} opacity="0.5">
          <text
            fontFamily={palette.labelFontFamily}
            fontSize={creditFontSize}
            fontStyle="italic"
            fill={palette.creditFill}
          >
            India per Survey of India boundary (datameet/maps) · neighbours per Natural Earth 1:50m · equirectangular projection
          </text>
        </g>
      </svg>

      {/* Zoom controls — overlaid in the top-right. onMouseDown stops the pan
          handler from firing when the user is just clicking a button. The
          bg-parchment-* class auto-themes to white in Broadsheet via the
          existing CSS override; border-ink-* themes likewise. */}
      <div
        className="absolute top-2 right-2 flex flex-col gap-1 z-10"
        onMouseDown={e => e.stopPropagation()}
      >
        <button
          type="button"
          aria-label="Zoom in"
          onClick={() => zoomByButton(BUTTON_ZOOM_STEP)}
          className="w-8 h-8 flex items-center justify-center text-base leading-none font-medium bg-parchment-50/90 hover:bg-parchment-50 border border-ink-300/30 rounded shadow-sm text-ink-100 transition cursor-pointer"
        >
          +
        </button>
        <button
          type="button"
          aria-label="Zoom out"
          onClick={() => zoomByButton(1 / BUTTON_ZOOM_STEP)}
          className="w-8 h-8 flex items-center justify-center text-base leading-none font-medium bg-parchment-50/90 hover:bg-parchment-50 border border-ink-300/30 rounded shadow-sm text-ink-100 transition cursor-pointer"
        >
          −
        </button>
        <button
          type="button"
          aria-label="Reset zoom to auto-fit"
          onClick={resetZoom}
          disabled={!isZoomed}
          className="w-8 h-8 flex items-center justify-center text-xs leading-none font-medium bg-parchment-50/90 hover:bg-parchment-50 border border-ink-300/30 rounded shadow-sm text-ink-100 transition cursor-pointer disabled:opacity-40 disabled:cursor-default"
          title="Reset to auto-fit"
        >
          ⤺
        </button>
      </div>
    </div>
  )
}
